import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { HandCoins, Upload } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { StatusBadge } from "../components/etams/StatusBadge";
import ProofUploader from "../components/etams/ProofUploader";
import { useDaftar } from "../hooks/useDaftar";
import { PalangDaftar, ThUrut, Paginasi } from "../components/etams/PalangDaftar";
import { formatIDR, formatDate, formatDateTime, todayISO } from "../lib/format";
import { apiErrorMessage } from "../lib/inventoryApi";
import UangLebihPemasokPanel from "./purchasing/UangLebihPemasokPanel";

// K212 (keputusan Owner 12 Okt 2026) — uang masuk yang dicatat Finance:
//   BUSINESS-80  laporan settlement DOKU diimpor per bulan → biaya potongan (MDR) per transaksi.
//   BUSINESS-83  uang yang dikembalikan pemasok (retur cara refund / uang muka ditagih balik).
// Finance mengimpor dan mencatat; Owner membaca. Penjaga sebenarnya di backend `routers/uang_masuk.py`.

const STATUS_SETTLEMENT = {
  COCOK: { label: "Cocok", tone: "success" },
  TIDAK_COCOK: { label: "Tidak cocok", tone: "danger" },
  TIDAK_DITEMUKAN: { label: "Tidak ditemukan", tone: "neutral" },
};
const URUT_SAH_BARIS = ["invoice_number", "tanggal_settlement", "nominal_kotor_idr", "mdr_idr", "status"];
const bulanIni = () => todayISO().slice(0, 7);

function KartuAngka({ judul, isi, sub, testId }) {
  return (
    <Card className="p-3" data-testid={testId}>
      <div className="text-xs text-muted-foreground">{judul}</div>
      <div className="text-lg font-semibold">{isi}</div>
      {sub ? <div className="text-xs text-muted-foreground">{sub}</div> : null}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// BUSINESS-80 — settlement DOKU
// ---------------------------------------------------------------------------
function SettlementDoku({ bolehTulis }) {
  const [bulan, setBulan] = useState(bulanIni());
  const [ringkasan, setRingkasan] = useState(null);
  const [status, setStatus] = useState("SEMUA");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [impor, setImpor] = useState(null);       // { berkas, pratinjau, pemetaan, hasil }
  const [sibuk, setSibuk] = useState(false);

  const daftar = useDaftar(items, {
    server: { total, urutSah: URUT_SAH_BARIS },
    urutAwal: { kunci: "invoice_number", arah: "asc" },
    awalan: "ds_",
  });
  const { cari, limit, offset, sort, arah } = daftar.paramServer;

  const params = useCallback((o = {}) => ({
    bulan, limit, offset, sort, arah, ...(cari && { cari }), ...(status !== "SEMUA" && { status }), ...o,
  }), [bulan, limit, offset, sort, arah, cari, status]);

  const muat = useCallback(async () => {
    if (!/^\d{4}-\d{2}$/.test(bulan)) return;
    try {
      const [r, b] = await Promise.all([
        api.get("/uang-masuk/doku-settlement/ringkasan", { params: { bulan } }),
        api.get("/uang-masuk/doku-settlement/baris", { params: params() }),
      ]);
      setRingkasan(r.data); setItems(b.data.items || []); setTotal(b.data.total ?? 0);
    } catch (e) { toast.error(apiErrorMessage(e, "Data settlement DOKU belum bisa dimuat.")); }
  }, [bulan, params]);
  useEffect(() => { muat(); }, [muat]);

  const ambilHalaman = useCallback(async (off, n) =>
    (await api.get("/uang-masuk/doku-settlement/baris", { params: params({ offset: off, limit: n }) })).data, [params]);

  const pilihBerkas = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setSibuk(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const r = await api.post("/uang-masuk/doku-settlement/pratinjau", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImpor({ berkas: f, pratinjau: r.data, pemetaan: { ...r.data.tebakan }, hasil: null });
    } catch (err) { toast.error(apiErrorMessage(err, "Berkas belum bisa dibaca.")); }
    finally { setSibuk(false); e.target.value = ""; }
  };

  const jalankanImpor = async () => {
    setSibuk(true);
    try {
      const fd = new FormData();
      fd.append("file", impor.berkas); fd.append("bulan", bulan); fd.append("pemetaan", JSON.stringify(impor.pemetaan));
      const r = await api.post("/uang-masuk/doku-settlement/impor", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const i = r.data.impor;
      toast.success(`Impor selesai: ${i.jumlah_cocok} cocok, ${i.jumlah_tidak_cocok} tidak cocok, ${i.jumlah_tidak_ditemukan} tidak ditemukan`);
      setImpor((x) => ({ ...x, hasil: r.data }));
      muat();
    } catch (err) { toast.error(apiErrorMessage(err, "Impor belum berhasil.")); }
    finally { setSibuk(false); }
  };

  const per = ringkasan?.per_status || {};
  const kolomBerkas = impor?.pratinjau?.kolom || [];
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <Label>Bulan settlement</Label>
          <Input type="month" value={bulan} onChange={(e) => setBulan(e.target.value)} className="w-44" data-testid="ds-bulan" />
        </div>
        {bolehTulis && (
          <label className="inline-flex items-center gap-2 cursor-pointer rounded-md border px-3 py-2 text-sm" data-testid="ds-pilih-berkas">
            <Upload size={14} /> {sibuk ? "Membaca berkas…" : "Impor laporan (CSV/XLSX)"}
            <input type="file" accept=".csv,.xlsx,text/csv" className="hidden" onChange={pilihBerkas} disabled={sibuk} />
          </label>
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        Unduh laporan settlement bulan ini dari Back Office DOKU, lalu impor di sini. Tiap baris dicocokkan lewat
        nomor invoice ke catatan pembayaran DOKU di ETAMSIOS; biaya potongan yang cocok tercatat per transaksi.
        Impor ulang bulan yang sama menimpa baris bernomor sama — tidak menggandakan.
      </p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KartuAngka testId="ds-cocok" judul="Cocok" isi={per.COCOK?.jumlah ?? 0} sub={`biaya ${formatIDR(per.COCOK?.mdr_idr || 0)}`} />
        <KartuAngka testId="ds-tidak-cocok" judul="Tidak cocok" isi={per.TIDAK_COCOK?.jumlah ?? 0} sub="perlu diperiksa" />
        <KartuAngka testId="ds-tidak-ditemukan" judul="Tidak ditemukan" isi={per.TIDAK_DITEMUKAN?.jumlah ?? 0} sub="bukan transaksi ETAMSIOS" />
        <KartuAngka testId="ds-total-biaya" judul="Biaya potongan tercatat" isi={formatIDR(ringkasan?.total_mdr_cocok_idr || 0)} />
      </div>
      {/* K217: biaya potongan per cara bayar (QRIS / Virtual Account per bank) — hanya baris yang cocok. */}
      {(ringkasan?.per_metode || []).length > 0 && (
        <div className="text-xs text-muted-foreground space-y-1" data-testid="ds-per-metode">
          <div className="font-medium">Biaya potongan per cara bayar</div>
          {ringkasan.per_metode.map((m) => (
            <div key={m.metode}>
              {m.metode}: {m.jumlah} transaksi · kotor {formatIDR(m.kotor_idr)} · biaya {formatIDR(m.mdr_idr)} · bersih {formatIDR(m.bersih_idr)}
            </div>
          ))}
        </div>
      )}

      <Card className="p-4">
        <PalangDaftar daftar={daftar} placeholder="Cari nomor invoice…" testId="ds-palang"
                      ekspor={{ nama: `settlement-doku-${bulan}`, ambilHalaman, kolom: [
                        { kunci: "invoice_number", judul: "Nomor invoice" },
                        { kunci: "tanggal_settlement", judul: "Tanggal settlement" },
                        { kunci: "metode_label", judul: "Cara bayar" },
                        { kunci: "nominal_kotor_idr", judul: "Nominal kotor" },
                        { kunci: "mdr_idr", judul: "Biaya potongan" },
                        { kunci: "nominal_bersih_idr", judul: "Nominal bersih" },
                        { kunci: "status", judul: "Status", ambil: (b) => STATUS_SETTLEMENT[b.status]?.label || b.status },
                        { kunci: "keterangan", judul: "Keterangan" },
                      ] }}>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-44 h-9" data-testid="ds-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="SEMUA">Semua status</SelectItem>
              {Object.entries(STATUS_SETTLEMENT).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </PalangDaftar>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="ds-tabel">
            <thead className="bg-muted/50 text-muted-foreground text-xs uppercase">
              <tr>
                <ThUrut daftar={daftar} kunci="invoice_number" className="text-left px-3 py-2">Nomor invoice</ThUrut>
                <ThUrut daftar={daftar} kunci="tanggal_settlement" className="text-left px-3 py-2">Tanggal</ThUrut>
                <th className="text-left px-3 py-2">Cara bayar</th>
                <ThUrut daftar={daftar} kunci="nominal_kotor_idr" className="text-right px-3 py-2" align="right">Kotor</ThUrut>
                <ThUrut daftar={daftar} kunci="mdr_idr" className="text-right px-3 py-2" align="right">Biaya</ThUrut>
                <th className="text-right px-3 py-2">Bersih</th>
                <ThUrut daftar={daftar} kunci="status" className="text-left px-3 py-2">Status</ThUrut>
              </tr>
            </thead>
            <tbody>
              {daftar.total === 0 && <tr><td colSpan={7} className="px-3 py-8 text-center text-muted-foreground">Belum ada laporan settlement untuk bulan ini.</td></tr>}
              {daftar.tampil.map((b) => (
                <tr key={b.id} className="border-t border-border">
                  <td className="px-3 py-2 font-mono text-xs">{b.invoice_number}</td>
                  <td className="px-3 py-2 text-xs">{b.tanggal_settlement ? formatDate(b.tanggal_settlement) : "—"}</td>
                  <td className="px-3 py-2 text-xs">{b.metode_label || "—"}</td>
                  <td className="px-3 py-2 text-right font-mono">{formatIDR(b.nominal_kotor_idr)}</td>
                  <td className="px-3 py-2 text-right font-mono">{formatIDR(b.mdr_idr)}</td>
                  <td className="px-3 py-2 text-right font-mono">{formatIDR(b.nominal_bersih_idr)}</td>
                  <td className="px-3 py-2">
                    <StatusBadge tone={STATUS_SETTLEMENT[b.status]?.tone}>{STATUS_SETTLEMENT[b.status]?.label || b.status}</StatusBadge>
                    {b.keterangan ? <div className="text-xs text-muted-foreground mt-1">{b.keterangan}</div> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Paginasi daftar={daftar} testId="ds-paginasi" />
        </div>
      </Card>

      {(ringkasan?.riwayat_impor || []).length > 0 && (
        <div className="text-xs text-muted-foreground space-y-1" data-testid="ds-riwayat">
          <div className="font-medium">Riwayat impor bulan ini</div>
          {ringkasan.riwayat_impor.map((r) => (
            <div key={r.id}>
              {formatDateTime(r.diimpor_pada)} · {r.nama_berkas} · {r.jumlah_baris} baris ({r.jumlah_cocok} cocok,
              {" "}{r.jumlah_tidak_cocok} tidak cocok, {r.jumlah_tidak_ditemukan} tidak ditemukan, {r.jumlah_dilewati} dilewati)
              {r.diimpor_oleh_nama ? ` · ${r.diimpor_oleh_nama}` : ""}
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!impor} onOpenChange={(o) => { if (!o) setImpor(null); }}>
        <DialogContent className="max-w-2xl" data-testid="ds-impor-modal">
          <DialogHeader><DialogTitle>Impor laporan settlement DOKU — {bulan}</DialogTitle></DialogHeader>
          {impor && !impor.hasil && (
            <div className="space-y-3 text-sm">
              <div>{impor.pratinjau.nama_berkas} · {impor.pratinjau.jumlah_baris} baris. Pilih kolom untuk tiap isian:</div>
              <div className="grid grid-cols-2 gap-2">
                {impor.pratinjau.bidang.map((b) => (
                  <div key={b.kode}>
                    <Label>{b.label}{b.wajib ? " *" : ""}</Label>
                    <Select value={impor.pemetaan[b.kode] || "__kosong"}
                            onValueChange={(v) => setImpor((x) => ({ ...x, pemetaan: { ...x.pemetaan, [b.kode]: v === "__kosong" ? null : v } }))}>
                      <SelectTrigger className="h-9" data-testid={`ds-peta-${b.kode}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__kosong">— tidak dipakai —</SelectItem>
                        {kolomBerkas.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
              <div className="text-xs text-muted-foreground">
                Bila biaya dan pajaknya terpisah di laporan, kosongkan kolom biaya dan pilih kolom nominal bersih —
                biaya dihitung dari nominal kotor dikurangi nominal bersih.
              </div>
              <div className="overflow-x-auto border rounded-md">
                <table className="w-full text-xs">
                  <thead className="bg-muted/50"><tr>{kolomBerkas.map((k) => <th key={k} className="px-2 py-1 text-left">{k}</th>)}</tr></thead>
                  <tbody>
                    {impor.pratinjau.contoh.map((b, i) => (
                      <tr key={i} className="border-t">{kolomBerkas.map((k) => <td key={k} className="px-2 py-1">{b[k]}</td>)}</tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {impor?.hasil && (
            <div className="space-y-2 text-sm" data-testid="ds-impor-hasil">
              <div>Selesai: {impor.hasil.impor.jumlah_cocok} cocok, {impor.hasil.impor.jumlah_tidak_cocok} tidak cocok,
                {" "}{impor.hasil.impor.jumlah_tidak_ditemukan} tidak ditemukan, {impor.hasil.impor.jumlah_dilewati} dilewati.</div>
              {impor.hasil.dilewati.length > 0 && (
                <div className="text-xs text-muted-foreground">
                  Baris yang dilewati: {impor.hasil.dilewati.slice(0, 20).map((d) => `baris ${d.baris_ke} (${d.alasan})`).join("; ")}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setImpor(null)}>{impor?.hasil ? "Tutup" : "Batal"}</Button>
            {impor && !impor.hasil && <Button onClick={jalankanImpor} disabled={sibuk} data-testid="ds-impor-jalankan">Impor</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// BUSINESS-83 — uang kembali pemasok
// ---------------------------------------------------------------------------
// K214 / BUSINESS-87: satu retur/uang muka boleh dicatat BEBERAPA kali sampai jumlah yang disepakati terpenuhi;
// selama masih kurang ia tetap "menunggu" dan sisanya ditagih Admin Purchasing.
// K215 / BUSINESS-90: uang yang masuk MELEBIHI jumlah disepakati tetap dicatat utuh; kelebihannya (uang lebih dari
// pemasok) dipilih Admin Purchasing — dikembalikan atau dipotongkan ke tagihan berikutnya — dan disetujui KC.
const KEADAAN = { MENUNGGU: { label: "Menunggu uang kembali", tone: "warning" }, TERCATAT: { label: "Diterima penuh", tone: "success" } };

function UangKembaliPemasok({ bolehTulis }) {
  const [keadaan, setKeadaan] = useState("MENUNGGU");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [catat, setCatat] = useState(null);
  const [isi, setIsi] = useState({ tanggal: todayISO(), nominal: "", bukti: null, catatan: "" });
  const [sibuk, setSibuk] = useState(false);
  const [segarkanLebih, setSegarkanLebih] = useState(0);

  const daftar = useDaftar(items, { server: { total, urutSah: [] }, awalan: "uk_" });
  const { cari, limit, offset } = daftar.paramServer;
  const params = useCallback((o = {}) => ({
    limit, offset, ...(cari && { cari }), ...(keadaan !== "SEMUA" && { keadaan }), ...o,
  }), [limit, offset, cari, keadaan]);

  const muat = useCallback(async () => {
    try {
      const r = await api.get("/uang-masuk/uang-kembali-vendor", { params: params() });
      setItems(r.data.items || []); setTotal(r.data.total ?? 0);
    } catch (e) { toast.error(apiErrorMessage(e, "Daftar uang kembali belum bisa dimuat.")); }
  }, [params]);
  useEffect(() => { muat(); }, [muat]);
  const ambilHalaman = useCallback(async (off, n) =>
    (await api.get("/uang-masuk/uang-kembali-vendor", { params: params({ offset: off, limit: n }) })).data, [params]);

  const buka = (s) => { setCatat(s); setIsi({ tanggal: todayISO(), nominal: String(s.sisa_idr || ""), bukti: null, catatan: "" }); };
  const lewatSisa = catat && Number(isi.nominal) > Number(catat.sisa_idr);
  const lebihBaru = lewatSisa ? Number(isi.nominal) - Number(catat.sisa_idr) : 0;
  const simpan = async () => {
    setSibuk(true);
    try {
      await api.post("/uang-masuk/uang-kembali-vendor", {
        sumber: catat.sumber, sumber_id: catat.sumber_id, tanggal_terima: isi.tanggal,
        nominal_idr: Number(isi.nominal), bukti_file_id: isi.bukti, catatan: isi.catatan || null,
      });
      toast.success("Uang kembali tercatat"); setCatat(null); muat(); setSegarkanLebih((n) => n + 1);
    } catch (e) { toast.error(apiErrorMessage(e, "Uang kembali belum bisa dicatat.")); }
    finally { setSibuk(false); }
  };
  const lihatBukti = async (id) => {
    try { const r = await api.get(`/uang-masuk/uang-kembali-vendor/${id}/bukti`); window.open(r.data.url, "_blank", "noopener"); }
    catch (e) { toast.error(apiErrorMessage(e, "Bukti belum bisa dibuka.")); }
  };
  const tautan = (s) => (s.sumber === "RETUR_VENDOR" ? "/purchasing/returns" : `/purchasing/dp/${s.sumber_id}`);

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">
        Retur ke pemasok yang diselesaikan dengan refund (sudah disetujui Kepala Cabang) dan uang muka yang ditagih
        balik menunggu di sini sampai Finance mencatat uang yang benar-benar masuk: tanggal, nominal, dan bukti mutasi.
        Bila pemasok mengirim bertahap, catat tiap kiriman; sisanya tetap menunggu dan ditagih Admin Purchasing.
        Bila pemasok mengirim lebih dari yang disepakati, catat sebesar uang yang masuk — kelebihannya tampil di
        daftar uang lebih di bawah dan diselesaikan Admin Purchasing bersama Kepala Cabang.
      </p>
      <Card className="p-4">
        <PalangDaftar daftar={daftar} placeholder="Cari nomor atau nama pemasok…" testId="uk-palang"
                      ekspor={{ nama: "uang-kembali-pemasok", ambilHalaman, kolom: [
                        { kunci: "sumber_label", judul: "Sumber" }, { kunci: "nomor", judul: "Nomor" },
                        { kunci: "vendor_nama", judul: "Pemasok" }, { kunci: "nilai_diharapkan_idr", judul: "Nilai" },
                        { kunci: "keadaan", judul: "Keadaan", ambil: (s) => KEADAAN[s.keadaan]?.label || s.keadaan },
                        { kunci: "total_diterima_idr", judul: "Sudah diterima" },
                        { kunci: "sisa_idr", judul: "Sisa" },
                        { kunci: "terakhir_diterima", judul: "Terakhir diterima", ambil: (s) => s.terakhir_diterima || "" },
                      ] }}>
          <Select value={keadaan} onValueChange={setKeadaan}>
            <SelectTrigger className="w-52 h-9" data-testid="uk-keadaan"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="MENUNGGU">Menunggu uang kembali</SelectItem>
              <SelectItem value="TERCATAT">Sudah diterima</SelectItem>
              <SelectItem value="SEMUA">Semua</SelectItem>
            </SelectContent>
          </Select>
        </PalangDaftar>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="uk-tabel">
            <thead className="bg-muted/50 text-muted-foreground text-xs uppercase">
              <tr>
                <ThUrut daftar={daftar} kunci="nomor" className="text-left px-3 py-2">Sumber</ThUrut>
                <ThUrut daftar={daftar} kunci="vendor_nama" className="text-left px-3 py-2">Pemasok</ThUrut>
                <ThUrut daftar={daftar} kunci="nilai_diharapkan_idr" className="text-right px-3 py-2" align="right">Nilai</ThUrut>
                <ThUrut daftar={daftar} kunci="keadaan" className="text-left px-3 py-2">Keadaan</ThUrut>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {daftar.total === 0 && <tr><td colSpan={5} className="px-3 py-8 text-center text-muted-foreground">Tidak ada uang kembali yang cocok dengan saringan ini.</td></tr>}
              {daftar.tampil.map((s) => (
                <tr key={`${s.sumber}-${s.sumber_id}`} className="border-t border-border" data-testid={`uk-baris-${s.sumber_id}`}>
                  <td className="px-3 py-2">
                    <div className="text-xs text-muted-foreground">{s.sumber_label}</div>
                    <Link to={tautan(s)} className="font-medium underline">{s.nomor || "—"}</Link>
                  </td>
                  <td className="px-3 py-2">{s.vendor_nama || "—"}</td>
                  <td className="px-3 py-2 text-right font-mono">{formatIDR(s.nilai_diharapkan_idr)}</td>
                  <td className="px-3 py-2">
                    <StatusBadge tone={KEADAAN[s.keadaan]?.tone}>{s.sebagian ? "Baru sebagian diterima" : KEADAAN[s.keadaan]?.label}</StatusBadge>
                    {(s.penerimaan || []).length > 0 && (
                      <div className="text-xs text-muted-foreground mt-1" data-testid={`uk-terima-${s.sumber_id}`}>
                        Diterima {formatIDR(s.total_diterima_idr)}{s.sisa_idr > 0 ? ` · sisa ${formatIDR(s.sisa_idr)}` : ""}
                        {s.lebih_idr > 0 ? ` · lebih ${formatIDR(s.lebih_idr)}` : ""}
                        {(s.penerimaan || []).map((c) => (
                          <div key={c.id}>
                            {formatDate(c.tanggal_terima)} · {formatIDR(c.nominal_idr)}{" "}
                            <button type="button" className="underline" onClick={() => lihatBukti(c.id)} data-testid={`uk-bukti-${c.id}`}>bukti</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {bolehTulis && (
                      <Button variant="ghost" size="sm" onClick={() => buka(s)} data-testid={`uk-catat-${s.sumber_id}`}>
                        {s.keadaan === "MENUNGGU" ? "Catat uang diterima" : "Catat uang masuk lagi"}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Paginasi daftar={daftar} testId="uk-paginasi" />
        </div>
      </Card>

      {/* K215 / BUSINESS-90: uang lebih dari pemasok — Finance mentransfer balik yang disetujui dikembalikan. */}
      <UangLebihPemasokPanel key={segarkanLebih} testId="uk-uang-lebih" />

      <Dialog open={!!catat} onOpenChange={(o) => { if (!o) setCatat(null); }}>
        <DialogContent data-testid="uk-modal">
          <DialogHeader><DialogTitle>Catat uang kembali — {catat?.nomor}</DialogTitle></DialogHeader>
          {catat && (
            <div className="space-y-3 text-sm">
              <div>{catat.sumber_label} · {catat.vendor_nama || "—"} · nilai {formatIDR(catat.nilai_diharapkan_idr)}</div>
              {catat.total_diterima_idr > 0 && (
                <div className="text-xs text-muted-foreground" data-testid="uk-sudah-diterima">
                  Sudah diterima {formatIDR(catat.total_diterima_idr)} · sisa {formatIDR(catat.sisa_idr)}
                </div>
              )}
              <ProofUploader testIdPrefix="uk-upload"
                             endpoint={`/uang-masuk/uang-kembali-vendor/bukti?sumber=${encodeURIComponent(catat.sumber)}&sumber_id=${encodeURIComponent(catat.sumber_id)}`}
                             onUploaded={(r) => setIsi((x) => ({ ...x, bukti: r.bukti_file_id }))} />
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Tanggal uang masuk *</Label>
                  <Input type="date" value={isi.tanggal} max={todayISO()} onChange={(e) => setIsi({ ...isi, tanggal: e.target.value })} data-testid="uk-tanggal" />
                </div>
                <div>
                  <Label>Nominal diterima (Rp) *</Label>
                  <Input inputMode="numeric" value={isi.nominal} onChange={(e) => setIsi({ ...isi, nominal: e.target.value.replace(/[^0-9]/g, "") })} data-testid="uk-nominal" />
                </div>
              </div>
              <Input placeholder="Catatan (mis. dipotong biaya transfer)" value={isi.catatan} onChange={(e) => setIsi({ ...isi, catatan: e.target.value })} />
              {lewatSisa ? (
                <div className="text-xs text-warning-soft-foreground" data-testid="uk-lewat-sisa">
                  Lebih {formatIDR(lebihBaru)} dari yang disepakati — tetap dicatat. Admin Purchasing memilih
                  penyelesaiannya: dikembalikan ke pemasok atau dipotongkan ke tagihan berikutnya pemasok yang sama.
                </div>
              ) : isi.nominal && Number(isi.nominal) < Number(catat.sisa_idr) ? (
                <div className="text-xs text-warning-soft-foreground" data-testid="uk-masih-sisa">
                  Masih kurang {formatIDR(Number(catat.sisa_idr) - Number(isi.nominal))} — tetap menunggu dan ditagih Admin Purchasing ke pemasok.
                </div>
              ) : null}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCatat(null)}>Batal</Button>
            <Button onClick={simpan} disabled={sibuk || !isi.bukti || !Number(isi.nominal)} data-testid="uk-simpan">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function UangMasuk() {
  const { roleCode } = useAuth();
  const bolehTulis = roleCode === "FINANCE";
  return (
    <div className="space-y-6" data-testid="uang-masuk-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><HandCoins size={22} /> Uang Masuk</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Biaya potongan DOKU dari laporan settlement bulanan, dan uang yang dikembalikan pemasok.
          {bolehTulis ? "" : " Hanya Finance yang mengimpor dan mencatat."}
        </p>
      </div>
      <Tabs defaultValue="doku">
        <TabsList>
          <TabsTrigger value="doku" data-testid="tab-doku">Settlement DOKU</TabsTrigger>
          <TabsTrigger value="pemasok" data-testid="tab-pemasok">Uang Kembali Pemasok</TabsTrigger>
        </TabsList>
        <TabsContent value="doku"><SettlementDoku bolehTulis={bolehTulis} /></TabsContent>
        <TabsContent value="pemasok"><UangKembaliPemasok bolehTulis={bolehTulis} /></TabsContent>
      </Tabs>
    </div>
  );
}
